/**
 * 认证状态管理
 * 使用Zustand管理用户认证状态
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api } from '../services/api'

interface User {
  id: number
  username: string
  email: string
  avatar?: string
  created_at: string
}

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  
  // Actions
  login: (username: string, password: string) => Promise<void>
  register: (username: string, email: string, password: string) => Promise<void>
  logout: () => void
  checkAuth: () => Promise<void>
  updateUser: (userData: Partial<User>) => void
  clearError: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        try {
          set({ isLoading: true, error: null })

          const response = await api.auth.login({ email, password })

          if (response.success) {
            const { access_token, refresh_token, user } = response.data

            // 保存令牌到 localStorage，Access Token 过期后由请求拦截器自动续期。
            localStorage.setItem('access_token', access_token)
            localStorage.setItem('refresh_token', refresh_token)
            localStorage.setItem('user_info', JSON.stringify(user))

            set({
              user,
              token: access_token,
              isAuthenticated: true,
              isLoading: false,
            })
          } else {
            throw new Error(response.message || '登录失败')
          }
        } catch (error: any) {
          // 提取具体的错误信息
          let errorMessage = '登录失败'

          if (error.response?.data?.detail) {
            errorMessage = error.response.data.detail
          } else if (error.response?.data?.message) {
            errorMessage = error.response.data.message
          } else if (error.message) {
            errorMessage = error.message
          }

          set({
            error: errorMessage,
            isLoading: false,
          })
          throw error
        }
      },

      register: async (username: string, email: string, password: string) => {
        try {
          set({ isLoading: true, error: null })
          
          const response = await api.auth.register({ username, email, password })
          
          if (response.success) {
            set({ isLoading: false })
          } else {
            throw new Error(response.message || '注册失败')
          }
        } catch (error: any) {
          set({
            error: error.message || '注册失败',
            isLoading: false,
          })
          throw error
        }
      },

      logout: () => {
        // 清除localStorage
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        localStorage.removeItem('user_info')
        
        // 清除状态
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          error: null,
        })
        
        // 跳转到登录页
        window.location.href = '/login'
      },

      checkAuth: async () => {
        try {
          const token = localStorage.getItem('access_token')
          const userInfo = localStorage.getItem('user_info')
          
          if (token && userInfo) {
            const user = JSON.parse(userInfo)
            
            // 验证token是否有效
            try {
              await api.users.me()
              
              set({
                user,
                token,
                isAuthenticated: true,
              })
            } catch (error) {
              // Token无效，清除状态
              get().logout()
            }
          } else {
            set({ isAuthenticated: false })
          }
        } catch (error) {
          console.error('检查认证状态失败:', error)
          set({ isAuthenticated: false })
        }
      },

      updateUser: (userData: Partial<User>) => {
        const currentUser = get().user
        if (currentUser) {
          const updatedUser = { ...currentUser, ...userData }
          localStorage.setItem('user_info', JSON.stringify(updatedUser))
          set({ user: updatedUser })
        }
      },

      clearError: () => {
        set({ error: null })
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)
